import React, { useState } from "react";
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Chip,
  Divider,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Grid
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import "./SFTPSchema.css";

function SFTPSchema({ SFTPItemID, SFTPName, SFTPSummaryText, SFTPColumnsData }) {
  const [expanded, setExpanded] = useState(false);

  const handleAccordionChange = () => {
    setExpanded(!expanded);
  };

  // Function to get color based on data type
  const getTypeColor = (type) => {
    switch (type.toLowerCase()) {
      case 'integer':
        return '#2196f3'; // Blue
      case 'float':
        return '#4caf50'; // Green
      case 'string':
        return '#ff9800'; // Orange
      case 'date':
        return '#9c27b0'; // Purple
      default:
        return '#757575'; // Grey
    }
  };

  return (
    <Box className="schema-container">
      {!SFTPItemID ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          Please select an SFTP connection from the sidebar to view schema details.
        </Alert>
      ) : (
        <>
          {/* Summary Section */}
          <Card sx={{ mb: 3, boxShadow: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <InfoIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" component="div" gutterBottom>
                  Dataset Summary
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body1" color="text.secondary" paragraph>
                {SFTPSummaryText || "No summary available for this dataset."}
              </Typography>
            </CardContent>
          </Card>

          {/* Columns Section */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2, display: 'flex', alignItems: 'center' }}>
            <Box component="span" sx={{ 
              width: 4, 
              height: 20, 
              bgcolor: 'primary.main', 
              display: 'inline-block', 
              mr: 1 
            }}/>
            Schema Definition
          </Typography>

          {SFTPColumnsData && SFTPColumnsData.length > 0 ? (
            <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
              <Table sx={{ minWidth: 650 }} aria-label="schema table">
                <TableHead sx={{ bgcolor: 'grey.100' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Column Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Data Type</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {SFTPColumnsData.map((column, index) => (
                    <TableRow
                      key={index}
                      sx={{ '&:nth-of-type(odd)': { bgcolor: 'rgba(0, 0, 0, 0.02)' } }}
                    >
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'medium' }}>
                        {column.name}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={column.type} 
                          size="small" 
                          sx={{ 
                            bgcolor: getTypeColor(column.type),
                            color: 'white',
                            fontWeight: 'bold'
                          }} 
                        />
                      </TableCell>
                      <TableCell>{column.description}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">
              No schema information available for this dataset.
            </Alert>
          )}

          {/* Additional Information Accordion */}
          <Accordion 
            expanded={expanded} 
            onChange={handleAccordionChange}
            sx={{ mt: 3, boxShadow: 2 }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              <Typography>Additional Information</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>Data Types Overview</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    <Chip label="integer" size="small" sx={{ bgcolor: getTypeColor('integer'), color: 'white' }} />
                    <Chip label="float" size="small" sx={{ bgcolor: getTypeColor('float'), color: 'white' }} />
                    <Chip label="string" size="small" sx={{ bgcolor: getTypeColor('string'), color: 'white' }} />
                    <Chip label="date" size="small" sx={{ bgcolor: getTypeColor('date'), color: 'white' }} />
                  </Box>
                  <Typography variant="body2" paragraph>
                    The schema uses standard data types to ensure consistency across the dataset.
                    Integer fields store whole numbers, float fields store decimal values, string fields
                    store text, and date fields store temporal information.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>Usage Notes</Typography>
                  <Typography variant="body2" paragraph>
                    This schema definition provides a comprehensive overview of the data structure.
                    When working with this dataset, ensure that your data processing tools correctly
                    interpret the data types to avoid conversion issues.
                  </Typography>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </>
      )}
    </Box>
  );
}

export default SFTPSchema;
